import React, { useState } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { LinkProps, useNavigate } from 'react-router-dom';
import { map } from 'lodash';

import { tOrdinalItem } from '@components/i18n';
import { ConfirmModal } from '@components/Base';
import { HealthQuote } from '@models/HealthQuote';

import { Title } from './title';

type MemberItemProps = {
  name: string;
  age: number;
}

const MemberItem = ({ name, age }: MemberItemProps) => {
  const { t } = useTranslation();

  return (
    <Card.Text className="d-flex justify-content-between mb-3">
      <strong>{name}</strong>
      <span>
        {t('user.yearsOld', { age: age })}
      </span>
    </Card.Text>
  );
};

type Props = Pick<HealthQuote, 'members'> & {
  formLinkTo: LinkProps['to'];
};

const MemberSection = ({ formLinkTo, members }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { me, spouse, sons, daughters } = members;

  return (
    <section className="py-5 bg-light">
      <Title>{t('healthQuoteDetailsPage.membersCovered.title')}</Title>
      <Container>
        <Row>
          <Col lg={5} className="mx-auto">
            <Card>
              <Card.Body>
                <MemberItem name={t('common:myself')} age={me.age} />

                {spouse && <MemberItem name={t('common:spouse')} age={spouse.age} />}

                {sons &&
                  map(sons, (son, idx) => (
                    <MemberItem
                      key={`sons.${idx}`}
                      name={tOrdinalItem(idx + 1, 'common:son')}
                      age={son.age}
                    />
                  ))}

                {daughters &&
                  map(daughters, (daughter, idx) => (
                    <MemberItem
                      key={`daughters.${idx}`}
                      name={tOrdinalItem(idx + 1, 'common:daughter')}
                      age={daughter.age}
                    />
                  ))}

                <div className="text-center mt-5">
                  <Button onClick={() => setShowDialog(true)} variant="primary">
                    {t('healthQuoteDetailsPage.membersCovered.edit')}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <ConfirmModal
        show={showDialog}
        onHide={() => setShowDialog(false)}
        onConfirm={() => navigate(formLinkTo, { replace: true })}
      />
    </section>
  );
};

export { MemberSection };
